import React, { type HTMLProps, type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import type { Row, Table } from '@tanstack/react-table';
import clsx from 'clsx';

type TableCheckboxProps = {
  children: ReactNode;
};

/**
 * A container element for the table inputs and label. Since these elements are not usually
 * rendered together, no div is rendered; it is intended for communication purposes so that the
 * elements can be grouped under a single namespace.
 */
function TableCheckbox({ children }: TableCheckboxProps): ReactElement {
  return <>{children}</>;
}

type IndeterminateInputProps = {
  indeterminate?: boolean;
} & HTMLProps<HTMLInputElement>;

/**
 * A checkout input that can be presented in an indeterminate state. Adapted from the example at:
 * https://tanstack.com/table/latest/docs/framework/react/examples/row-selection
 */
function IndeterminateInput({
  indeterminate,
  className = '',
  ...rest
}: IndeterminateInputProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={clsx('form-check-input cursor-pointer', className)}
      {...rest}
    />
  );
}

type HeaderInputProps<TData> = {
  id: string;
  table: Table<TData>;
};

/**
 * A checkbox input that can be used in a table header for selecting/de-selecting all rows in the
 * table.
 *
 * It is never presented as a switch in the UI, so the role attribute is not needed and can be
 * omitted.
 */
function HeaderInput<TData extends object>({ id, table }: HeaderInputProps<TData>): ReactElement {
  return (
    <IndeterminateInput
      id={id}
      checked={table.getIsAllPageRowsSelected()}
      indeterminate={table.getIsSomePageRowsSelected()}
      onChange={table.getToggleAllPageRowsSelectedHandler()}
    />
  );
}

type RowInputProps<TData> = {
  id: string;
  row: Row<TData>;
  disabled?: boolean;
};

/**
 * A checkbox input that can be used in a table cell for selecting a row.
 *
 * It is never presented as a switch in the UI, so the role attribute is not needed and can be
 * omitted.
 */
export function RowInput<TData extends object>({
  id,
  row,
  disabled
}: RowInputProps<TData>): ReactElement {
  return (
    <IndeterminateInput
      id={id}
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect() || disabled}
      indeterminate={row.getIsSomeSelected()}
      onChange={row.getToggleSelectedHandler()}
    />
  );
}

type RowLabelProps = {
  htmlFor: string;
  children?: ReactNode;
};

/**
 * A checkbox label that can be used in a table cell for selecting a row.
 */
export function RowLabel({ htmlFor, children }: RowLabelProps): ReactElement {
  return (
    <label htmlFor={htmlFor} className="form-check-label">
      {children}
    </label>
  );
}

export default Object.assign(TableCheckbox, {
  HeaderInput,
  RowInput,
  RowLabel
});
